/**
 * MemberTag - WhatsApp-like member identification badge
 * 
 * Shows Wing/Flat number and verification status for community members
 * Used in forums, discussions, and correspondence to identify verified buyers
 */

import { Badge } from '@/components/ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Home, ShieldCheck, ShieldAlert, Clock, Building2 } from 'lucide-react';

/**
 * MemberTag Props:
 * @param {string} flatNumber - The flat/unit number (e.g., "A-1201", "Tower 1-505")
 * @param {string} wing - Optional wing/tower name
 * @param {string} status - Verification status: 'verified', 'pending', 'unverified', 'group_admin'
 * @param {string} name - Member name (optional, for tooltip)
 * @param {boolean} showName - Whether to show the name alongside the tag
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} compact - Compact mode for list views
 */
export function MemberTag({ 
  flatNumber, 
  wing, 
  status = 'unverified', 
  name,
  showName = false,
  size = 'md',
  compact = false 
}) {
  const displayUnit = wing ? `${wing}-${flatNumber}` : flatNumber;
  
  const statusConfig = {
    verified: {
      icon: ShieldCheck,
      color: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400',
      label: 'Verified Buyer',
      dotColor: 'bg-green-500'
    },
    group_admin: {
      icon: Building2,
      color: 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400',
      label: 'Group Admin',
      dotColor: 'bg-indigo-500'
    },
    pending: {
      icon: Clock,
      color: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400',
      label: 'Verification Pending',
      dotColor: 'bg-amber-500'
    },
    unverified: {
      icon: ShieldAlert,
      color: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400',
      label: 'Not Verified',
      dotColor: 'bg-gray-400'
    }
  };

  const config = statusConfig[status] || statusConfig.unverified;
  const StatusIcon = config.icon;

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-1 gap-1.5',
    lg: 'text-sm px-2.5 py-1.5 gap-2'
  };

  const iconSizes = {
    sm: 'h-2.5 w-2.5',
    md: 'h-3 w-3',
    lg: 'h-4 w-4'
  };

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`inline-flex items-center gap-1 ${sizeClasses.sm} rounded-full border ${config.color}`}>
              <span className={`${config.dotColor} w-1.5 h-1.5 rounded-full`} />
              <Home className="h-2.5 w-2.5" />
              <span className="font-medium">{displayUnit}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs">
              <div className="font-medium">{name || 'Member'}</div>
              <div className="flex items-center gap-1 mt-1">
                <StatusIcon className="h-3 w-3" />
                <span>{config.label}</span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`inline-flex items-center ${sizeClasses[size]} rounded-full border ${config.color} font-medium`}
            data-testid={`member-tag-${displayUnit}`}
          >
            <Home className={iconSizes[size]} />
            <span>{displayUnit}</span>
            <StatusIcon className={iconSizes[size]} />
            {showName && name && (
              <span className="ml-1 text-muted-foreground font-normal">({name})</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="text-xs space-y-1">
            {name && <div className="font-semibold">{name}</div>}
            <div className="flex items-center gap-1.5">
              <Home className="h-3 w-3 text-muted-foreground" />
              <span>Unit: {displayUnit}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusIcon className="h-3 w-3" />
              <span>{config.label}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * MemberTagInline - For use in text/chat messages
 */
export function MemberTagInline({ flatNumber, wing, status = 'verified' }) {
  const displayUnit = wing ? `${wing}-${flatNumber}` : flatNumber;
  
  const dotColors = {
    verified: 'bg-green-500',
    group_admin: 'bg-indigo-500',
    pending: 'bg-amber-500',
    unverified: 'bg-gray-400'
  };

  return (
    <span className="inline-flex items-center gap-1 text-xs bg-muted px-1.5 py-0.5 rounded font-medium">
      <span className={`${dotColors[status] || dotColors.unverified} w-1.5 h-1.5 rounded-full`} />
      {displayUnit}
    </span>
  );
}

/**
 * AuthorTag - For forum posts and contributions
 */
export function AuthorTag({ 
  name, 
  flatNumber, 
  wing, 
  status = 'unverified',
  timestamp,
  isAdmin = false 
}) {
  const displayUnit = flatNumber ? (wing ? `${wing}-${flatNumber}` : flatNumber) : null;
  
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-sm">{name || 'Anonymous'}</span>
      
      {displayUnit && (
        <MemberTag 
          flatNumber={flatNumber} 
          wing={wing} 
          status={status} 
          size="sm"
          compact
        />
      )}
      
      {isAdmin && (
        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
          Admin
        </Badge>
      )}
      
      {timestamp && (
        <span className="text-xs text-muted-foreground">
          {new Date(timestamp).toLocaleDateString('en-IN', { 
            day: 'numeric', 
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      )}
    </div>
  );
}

export default MemberTag;
